import '../assets/beautrium-css/home.scss'
import '../assets/beautrium-css/strapi.scss'
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import queryString from 'query-string';
import Page from '../components/Page';
import { getPageLayout, getPageLayoutStrapi } from '../actions/layout/index'
import { renderLayout } from '../assets/util.layout.strapi';
// import moment from 'moment';
const frontload = async props => {
    const query = queryString.parse(props.location.search);
    const layoutId = (props.authenticated && _.get(props, "profile.role_id", 0) === 4 && query.preview) ? query.layout_id : null;

    // Only load if we don't already have layout data
    if (!props.layoutComponents || props.layoutComponents.length === 0) {
        return await props.getPageLayoutStrapi("home", layoutId);
    }
    return null;
}

/**
 * Home Component
 *
 * This component represents the homepage of the application. It handles
 * rendering layout components dynamically based on the provided props and
 * manages navigation and layout updates based on query parameters.
 *
 * @extends {Component}
 *
 * @component
 * @example
 * <Home
 *   location={location}
 *   history={history}
 *   authenticated={true}
 *   profile={{ role_id: 4 }}
 *   layoutComponents={[{ type: 'header', content: 'Welcome' }]}
 *   getPageLayoutStrapi={(page, layoutId) => {}}
 * />
 *
 * @property {Object} props - The properties passed to the component.
 * @property {Object} props.location - The location object from React Router.
 * @property {string} props.location.search - The query string from the URL.
 * @property {Object} props.history - The history object from React Router for navigation.
 * @property {boolean} props.authenticated - Indicates if the user is authenticated.
 * @property {Object} props.profile - The user's profile information.
 * @property {number} props.profile.role_id - The role ID of the user.
 * @property {Array} props.layoutComponents - An array of layout components to render.
 * @property {Function} props.getPageLayoutStrapi - A function to fetch the page layout from Strapi.
 *
 * @method componentDidMount
 * Handles redirection if the user is not authenticated and tries to access a preview.
 *
 * @method componentDidUpdate
 * Handles updates to the layout when query parameters change.
 *
 * @method render
 * Renders the Home component. If `layoutComponents` are provided, it renders the layout
 * using the `renderLayout` function. Otherwise, it renders nothing.
 *
 * @returns {JSX.Element} The rendered Home component.
 */
class Home extends Component {
    constructor(props) {
        super(props);
        const hasLayoutComponents = props.layoutComponents && props.layoutComponents.length > 0;
        this.state = {
            isContentLoaded: hasLayoutComponents, // If components are available, content is loaded
            showSkeleton: !hasLayoutComponents, // Only show skeleton if no components
            isLayoutLoading: false
        };
    }

    componentDidMount() {
        const query = queryString.parse(this.props.location.search);
        if (query.preview && !this.props.authenticated && _.get(this.props, "profile.role_id", 0) !== 4) {
            this.props.history.push('/');
        }

        // Note: Layout fetching is handled by frontload function
        // No need to duplicate the fetch here

        // Handle case when layout components are already available
        if (this.props.layoutComponents && this.props.layoutComponents.length > 0) {
            // First fade out skeleton completely
            this.setState({ showSkeleton: false });

            // Then show content after skeleton is hidden
            setTimeout(() => {
                this.setState({ isContentLoaded: true });
            }, 100); // Wait for skeleton fade out animation to complete
        }
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.location.search, this.props.location.search)) {
            const prevQuery = queryString.parse(prevProps.location.search);
            const query = queryString.parse(this.props.location.search);
            if (prevQuery.preview !== query.preview || prevQuery.layout_id !== query.layout_id) {
                this.setState({ showSkeleton: true, isContentLoaded: false });
                if (!this.state.isLayoutLoading) {
                    this.setState({ isLayoutLoading: true });
                    this.props.getPageLayoutStrapi("home", (this.props.authenticated && _.get(this.props, "profile.role_id", 0) === 4 && query.preview) ? query.layout_id : null)
                        .finally(() => this.setState({ isLayoutLoading: false }));
                }
            }
        }

        // Handle content loading state
        if (prevProps.layoutComponents !== this.props.layoutComponents) {
            if (this.props.layoutComponents && this.props.layoutComponents.length > 0) {
                // First fade out skeleton completely
                this.setState({ showSkeleton: false });

                // Then show content after skeleton is hidden
                setTimeout(() => {
                    this.setState({ isContentLoaded: true });
                }, 100); // Wait for skeleton fade out animation to complete
            } else if (!this.props.layoutComponents || this.props.layoutComponents.length === 0) {
                // Only fetch if we don't have components AND we're not already loading
                // AND this is not the initial load (prevProps.layoutComponents should be undefined initially)
                if (!this.state.isLayoutLoading && prevProps.layoutComponents !== undefined) {
                    this.setState({ showSkeleton: true, isContentLoaded: false });

                    // Fetch layout if components are empty and not already loading
                    this.setState({ isLayoutLoading: true });
                    const query = queryString.parse(this.props.location.search);
                    const layoutId = (this.props.authenticated && _.get(this.props, "profile.role_id", 0) === 4 && query.preview) ? query.layout_id : null;
                    this.props.getPageLayoutStrapi("home", layoutId)
                        .finally(() => this.setState({ isLayoutLoading: false }));
                }
            }
        }

        // Additional check for when layoutComponents become empty (e.g., after navigation)
        if (prevProps.layoutComponents && prevProps.layoutComponents.length > 0 &&
            (!this.props.layoutComponents || this.props.layoutComponents.length === 0)) {
            if (!this.state.isLayoutLoading) {
                this.setState({ isLayoutLoading: true });
                const query = queryString.parse(this.props.location.search);
                const layoutId = (this.props.authenticated && _.get(this.props, "profile.role_id", 0) === 4 && query.preview) ? query.layout_id : null;
                this.props.getPageLayoutStrapi("home", layoutId)
                    .finally(() => this.setState({ isLayoutLoading: false }));
            }
        }
    }

    /**
     * Renders the Home component.
     *
     * @returns {JSX.Element} The rendered Home component.
     * @property {Array} layoutComponents - An array of layout components to be rendered.
     * If `layoutComponents` is provided and contains elements, it will render the layout using `renderLayout`.
     * Otherwise, it will render nothing.
     */
    render() {
        const { layoutComponents } = this.props; //new_mats, best_mats, promotion_section
        const { showSkeleton, isContentLoaded } = this.state;

        // If no layout components, ensure skeleton is shown
        if (!layoutComponents || layoutComponents.length === 0) {
            // Ensure skeleton is shown
            if (!showSkeleton) {
                setTimeout(() => {
                    this.setState({ showSkeleton: true, isContentLoaded: false });
                }, 0);
            }
        } else {
            // If we have layout components, ensure skeleton is hidden when content is loaded
            if (showSkeleton && isContentLoaded) {
                setTimeout(() => {
                    this.setState({ showSkeleton: false });
                }, 0);
            }
        }

        // const query = queryString.parse(search)
        return (
            <Page id="homepage" className="" style={{ minHeight: '100vh' }}>
                {/* <CouponSection history={this.props.history} /> */}

                {/* Render actual content - only when skeleton is hidden */}
                {(layoutComponents && layoutComponents.length > 0 && !showSkeleton) && (
                    <div
                        className={`home-content ${isContentLoaded ? 'content-loaded' : ''}`}
                        style={{
                            opacity: isContentLoaded ? 1 : 0,
                            transition: 'opacity 0.3s ease-in-out'
                        }}
                    >
                        {renderLayout(layoutComponents)}
                    </div>
                )}

                {/* Render skeleton loading state */}
                {showSkeleton && (
                    <div className={`home-skeleton ${isContentLoaded ? 'fade-out' : ''}`}>
                        {/* HomeCarousel Skeleton */}
                        <section className="container-fluid skeleton-carousel">
                            <div className="row">
                                <div className="col-12 px-0">
                                    <div className="carousel slide carousel-fade">
                                        <ol className="carousel-indicators">
                                            <li className="active"></li>
                                            <li></li>
                                            <li></li>
                                        </ol>
                                        <div className="carousel-inner">
                                            <div className="carousel-item active">
                                                <div className="skeleton-banner"></div>
                                            </div>
                                        </div>
                                        <div className="carousel-control-prev" role="button">
                                            <span className="skeleton-arrow"></span>
                                        </div>
                                        <div className="carousel-control-next" role="button">
                                            <span className="skeleton-arrow"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Categories Skeleton */}
                        <section className="home_categories skeleton-categories">
                            <div className="container px-0">
                                <div className="row no-gutters nowrap-scroll justify-content-between">
                                    {Array.from({ length: 8 }, (_, index) => (
                                        <div key={`skeleton-category-${index}`} className="category text-center px-1 py-1">
                                            <div className="skeleton-category-image"></div>
                                            <div className="skeleton-category-title"></div>
                                            <div className="skeleton-category-subtitle mb-3"></div>
                                            <div className="skeleton-category-image"></div>
                                            <div className="skeleton-category-title"></div>
                                            <div className="skeleton-category-subtitle"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Grid1 Skeleton */}
                        <section className="container-fluid skeleton-grid">
                            <div className="container">
                                <div className="row">
                                    <div className="col-12" style={{ marginBottom: 15 }}>
                                        <div className="skeleton-grid-title"></div>
                                        <div className="skeleton-view-all"></div>
                                    </div>
                                    <div className="col-12">
                                        <div className="skeleton-grid-content"></div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </Page>
        );
    }
}

function mapStateToProps(state) {
    return {
        authenticated: state.auth.authenticated,
        profile: state.prof.profile,
        layoutComponents: state.layout.layouts ? state.layout.layouts.home : undefined,
    }
}
export default connect(mapStateToProps, { getPageLayoutStrapi, getPageLayout })(
    frontloadConnect(frontload, {
        onMount: true,
        onUpdate: false,
        noServerRender: false,
        serverPromise: true
    })(Home)
) //getNewArrivals, setFilter, setSort, getTrendyProducts, getPromotionProducts, 
